import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import {
  Create,
  Edit,
  List,
  Show,
  SimpleForm,
  SimpleShowLayout,
  TextField,
  TextInput,
  SelectInput,
  Toolbar,
  SaveButton,
  DeleteButton,
  BooleanField,
  required,
  minLength,
  showNotification,
  refreshView,
} from 'react-admin';
import { withStyles, FormHelperText, Button } from '@material-ui/core';
import Datagrid from './detail/Datagrid';
import ShowButton from './button/ShowButton';
import { generatePassword } from '../utils';
import { resetUserTotp } from '../api';

function getJwtClaims() {
  try {
    const token = window.localStorage.getItem('jsonWebToken');
    if (!token) return null;
    return JSON.parse(atob(token.split('.')[1]));
  } catch (e) {
    return null;
  }
}

function getCurrentUserRole() {
  const claims = getJwtClaims();
  return claims && (claims['https://hasura.io/jwt/claims'] || {})['x-hasura-default-role'];
}

function getCurrentUserId() {
  const claims = getJwtClaims();
  const raw = claims && (claims['https://hasura.io/jwt/claims'] || {})['x-hasura-user-id'];
  return raw ? parseInt(raw, 10) : null;
}

const RoleInput = props => {
  const isSelf = props.record && props.record.id === getCurrentUserId();
  return (
    <SelectInput
      {...props}
      source="role"
      label="Role"
      disabled={isSelf}
      helperText={isSelf ? 'You cannot change your own role' : undefined}
      choices={[
        { id: 'admin', name: 'Admin' },
        { id: 'editor', name: 'Editor' },
      ]}
    />
  );
};

const dangerButtonStyles = theme => ({
  danger: {
    color: theme.palette.error.main,
    borderColor: theme.palette.error.main,
    '&:hover': {
      borderColor: theme.palette.error.dark,
      backgroundColor: 'rgba(244, 67, 54, 0.04)',
    },
  },
});

class ResetTotpButtonImpl extends Component {
  state = { busy: false };

  handleClick = async () => {
    const { record, dispatch } = this.props;
    if (!record || !record.id) return;
    if (!window.confirm(`Reset 2FA for "${record.username}"? They will be required to set it up again on next login.`)) {
      return;
    }
    this.setState({ busy: true });
    try {
      await resetUserTotp({ userId: record.id });
      dispatch(showNotification('2FA reset successfully', 'info'));
      dispatch(refreshView());
    } catch (err) {
      dispatch(showNotification(err.message || '2FA reset failed', 'warning'));
    } finally {
      this.setState({ busy: false });
    }
  };

  render() {
    const { record, classes } = this.props;
    if (getCurrentUserRole() !== 'admin') return null;
    if (!record || !record.totpEnabled) return null;
    return (
      <Button
        variant="outlined"
        className={classes.danger}
        onClick={this.handleClick}
        disabled={this.state.busy}
      >
        Reset 2FA
      </Button>
    );
  }
}
ResetTotpButtonImpl.propTypes = {
  record: PropTypes.object,
  dispatch: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
};
const ResetTotpButton = connect()(withStyles(dangerButtonStyles)(ResetTotpButtonImpl));

const toolbarStyles = {
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
  },
};

const TITLE_SINGULAR = 'User';
const TITLE_PLURAL = 'Users';

const UserTitle = ({ record }) => (
  <span>
    {TITLE_SINGULAR} "{record.username}"
  </span>
);
UserTitle.propTypes = { record: PropTypes.shape({ username: PropTypes.string }) };

export const UserList = props => (
  <List title={TITLE_PLURAL} {...props} sort={{ field: 'username', order: 'ASC' }}>
    <Datagrid rowClick="edit">
      <TextField source="username" />
      <TextField source="role" />
      <BooleanField source="totpEnabled" label="2FA" />
      <ShowButton />
    </Datagrid>
  </List>
);

export const UserShow = props => (
  <Show title={<UserTitle />} {...props}>
    <SimpleShowLayout>
      <TextField source="role" />
      <TextField source="username" />
      <BooleanField source="totpEnabled" label="2FA enabled" />
      <ResetTotpButton />
    </SimpleShowLayout>
  </Show>
);

const UserEditToolbar = withStyles(toolbarStyles)(({ ...restProps }) => {
  const isAdmin = getCurrentUserRole() === 'admin';
  const isSelf = restProps.record && restProps.record.id === getCurrentUserId();
  return (
    <Toolbar {...restProps}>
      <SaveButton />
      {isAdmin && !isSelf && <DeleteButton undoable={false} />}
    </Toolbar>
  );
});
UserEditToolbar.displayName = 'UserEditToolbar';

export const UserEdit = props => (
  <Edit title={<UserTitle />} {...props}>
    <SimpleForm toolbar={<UserEditToolbar />}>
      <RoleInput validate={[required()]} />
      <TextInput source="username" validate={[required()]} />
      <BooleanField source="totpEnabled" label="2FA enabled" />
      <ResetTotpButton />
    </SimpleForm>
  </Edit>
);

const UserCreateToolbar = withRouter(
  connect()(
    withStyles(toolbarStyles)(props => {
      const { dispatch, history, ...restProps } = props;
      const { handleSubmit } = restProps;
      const handleSave = () => {
        return handleSubmit(async values => {
          await dispatch({ type: 'CREATE_USER', payload: values });
        });
      };
      return (
        <Toolbar {...restProps}>
          <SaveButton handleSubmitWithRedirect={handleSave} />
        </Toolbar>
      );
    }),
  ),
);
UserCreateToolbar.displayName = 'UserCreateToolbar';

export const UserCreate = props => (
  <Create {...props}>
    <SimpleForm toolbar={<UserCreateToolbar />}>
      <SelectInput
        source="role"
        defaultValue="editor"
        validate={[required()]}
        choices={[
          { id: 'editor', name: 'Editor' },
          { id: 'admin', name: 'Admin' },
        ]}
      />
      <TextInput source="username" validate={[required()]} />
      <TextInput
        source="password"
        defaultValue={generatePassword(16)}
        validate={[required(), minLength(8)]}
      />
      <FormHelperText disabled>
        WARNING: Store this password somewhere safe before saving as it will be impossible to
        recover afterwards.
      </FormHelperText>
    </SimpleForm>
  </Create>
);
